/**
 * ChangePaymentMethodModal — W2.6
 *
 * Modal mit Stripe-CardElement für Zahlungsmethoden-Update.
 * PATCH /api/v2/subscription/payment-method via updatePaymentMethod-Prop.
 */
import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { Stripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'

let stripePromise: Promise<Stripe | null> | null = null

function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    // '/pure' statt '@stripe/stripe-js' — siehe Begruendung in CheckoutModal.tsx:
    // der Default-Entrypoint injiziert js.stripe.com bereits beim Import und lief
    // durch das Chunk-Merging bei jedem App-Boot.
    stripePromise = import('@stripe/stripe-js/pure').then(({ loadStripe }) =>
      loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY ?? '')
    )
  }
  return stripePromise
}

function getStripeTheme(): 'night' | 'stripe' {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'night' : 'stripe'
}

type FormProps = {
  onSuccess: () => void
  onClose: () => void
  updatePaymentMethod: (stripeToken: string) => Promise<void>
}

function ChangePaymentForm({ onSuccess, onClose, updatePaymentMethod }: FormProps) {
  const { t } = useTranslation('subscription')
  const stripe = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)
  const [cardError, setCardError] = useState<string | null>(null)

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return
    setIsLoading(true)
    setCardError(null)

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) { setIsLoading(false); return }

    const result = await stripe.createToken(cardElement)
    if (result.error) {
      setCardError(result.error.message ?? t('changePaymentModal.cardError'))
      setIsLoading(false)
      return
    }

    try {
      await updatePaymentMethod(result.token!.id)
      onSuccess()
    } catch {
      setCardError(t('changePaymentModal.updateError'))
    } finally {
      setIsLoading(false)
    }
  }, [stripe, elements, updatePaymentMethod, onSuccess, t])

  return (
    <div data-testid="change-payment-form">
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '10px 12px',
          marginBottom: 16,
          background: 'var(--input-bg, var(--surface))',
          width: '100%',
        }}
        data-testid="card-element-payment-wrapper"
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '14px',
                color: 'var(--text)',
                '::placeholder': { color: 'var(--text-subtle)' },
              },
            },
          }}
        />
      </div>

      {cardError && (
        <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          {cardError}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onClose} disabled={isLoading}>
          {t('common:actions.cancel')}
        </button>
        <button
          className="btn btn-primary"
          onClick={() => { void handleSubmit() }}
          disabled={isLoading || !stripe}
          data-testid="payment-method-submit"
        >
          {isLoading ? t('changePaymentModal.saveButtonLoading') : t('changePaymentModal.saveButton')}
        </button>
      </div>
    </div>
  )
}

type ChangePaymentMethodModalProps = {
  onSuccess: () => void
  onClose: () => void
  updatePaymentMethod: (stripeToken: string) => Promise<void>
}

export function ChangePaymentMethodModal({
  onSuccess,
  onClose,
  updatePaymentMethod,
}: ChangePaymentMethodModalProps) {
  const { t } = useTranslation('subscription')
  const [stripeInstance] = useState<Promise<Stripe | null>>(getStripePromise)
  const appearance = {
    theme: getStripeTheme(),
    variables: {
      fontFamily: 'var(--font-body, sans-serif)',
      colorPrimary: 'var(--c-primary, #6366f1)',
    },
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('changePaymentModal.ariaLabel')}
      data-testid="change-payment-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        padding: 16,
      }}
    >
      <div
        className="card"
        style={{ width: '100%', maxWidth: 440, padding: 24 }}
      >
        <h2 style={{ marginBottom: 16, fontSize: 18 }}>{t('changePaymentModal.title')}</h2>
        <Elements stripe={stripeInstance} options={{ appearance: appearance as import('@stripe/stripe-js').Appearance }}>
          <ChangePaymentForm
            onSuccess={onSuccess}
            onClose={onClose}
            updatePaymentMethod={updatePaymentMethod}
          />
        </Elements>
      </div>
    </div>
  )
}
